// Pure UI-only state for the in-app native PDF preview overlay — which generated document
// (if any) is currently being previewed. No API calls, no persistence: unlike
// `useBiometricLock`'s `enabled`/`isAvailable`, nothing here survives a reload, nor should
// it. Lives directly under `src/hooks/` rather than `src/hooks/featues/` (reserved for
// React-Query-backed backend domains).
import { useEffect, useState } from 'react'

export interface PdfPreviewData {
  title: string
  pageDataUrls: string[]
  blob: Blob
  filename: string
}

// `openPdfPreview`/`closePdfPreview` are called imperatively from `src/utils/exportUtils.ts`
// (a plain `.ts` file with no hook context) once a native PDF has finished rasterizing — a
// plain module-level store + listener list keeps every hook instance in sync the moment the
// active preview changes, without adding a state library (mirrors the listener-list pattern
// already used by `src/hooks/useBiometricLock.ts`).
const listeners = new Set<(preview: PdfPreviewData | null) => void>()
let previewState: PdfPreviewData | null = null

export function openPdfPreview(data: PdfPreviewData): void {
  previewState = data
  listeners.forEach((listener) => listener(previewState))
}

export function closePdfPreview(): void {
  previewState = null
  listeners.forEach((listener) => listener(previewState))
}

export function usePdfPreview(): PdfPreviewData | null {
  const [preview, setPreview] = useState(previewState)

  useEffect(() => {
    listeners.add(setPreview)
    return () => {
      listeners.delete(setPreview)
    }
  }, [])

  return preview
}
